import React, { useEffect, useRef } from 'react'
import { Avatar, Card, Skeleton, Space, Tag, Typography } from 'antd'
import { Icon } from '@iconify/react'

import HomeCardHeader from './HomeCardHeader'
import styles from './PeopleCard.module.css'
import shellStyles from './HomeCardShell.module.css'

const { Text } = Typography

const PEOPLE_ROW_SKELETON_COUNT = 6

const SKELETON_NAME_WIDTHS_PX = [148, 192, 124, 168, 204, 156]

const SKELETON_EMAIL_WIDTHS_PX = [200, 164, 220, 188, 152, 176]

const PeopleRowSkeleton = ({ rowIndex }) => {
    const nameW =
        SKELETON_NAME_WIDTHS_PX[rowIndex % SKELETON_NAME_WIDTHS_PX.length]
    const emailW =
        SKELETON_EMAIL_WIDTHS_PX[rowIndex % SKELETON_EMAIL_WIDTHS_PX.length]

    return (
        <div
            className={`${styles.peopleRow} ${styles.peopleRowSkeleton}`}
            aria-hidden
        >
            <div className={styles.peopleLeft}>
                <Skeleton.Avatar active size={34} />
                <div className={styles.peopleMeta}>
                    <Skeleton.Input
                        active
                        size="small"
                        className={styles.skeletonPeopleName}
                        style={{ width: nameW, height: 16 }}
                    />
                    <Skeleton.Input
                        active
                        size="small"
                        className={styles.skeletonPeopleEmail}
                        style={{ width: emailW, height: 13 }}
                    />
                </div>
            </div>
            <div className={styles.peopleTagsSkeleton}>
                <Skeleton.Button
                    active
                    size="small"
                    className={styles.skeletonStatChip}
                />
                <Skeleton.Button
                    active
                    size="small"
                    className={`${styles.skeletonStatChip} ${styles.skeletonStatChipMid}`}
                />
                <Skeleton.Button
                    active
                    size="small"
                    className={`${styles.skeletonStatChip} ${styles.skeletonStatChipLast}`}
                />
            </div>
        </div>
    )
}

const PeopleCard = ({
    people,
    peopleStatus,
    peopleError,
    peopleTotal,
    peopleHasNext,
    onRetry,
    onLoadMore,
}) => {
    const scrollRef = useRef(null)
    const sentinelRef = useRef(null)

    useEffect(() => {
        if (!sentinelRef.current || !scrollRef.current || !peopleHasNext) return
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) onLoadMore?.()
            },
            {
                root: scrollRef.current,
                rootMargin: '140px 0px',
                threshold: 0.1,
            }
        )
        observer.observe(sentinelRef.current)
        return () => observer.disconnect()
    }, [peopleHasNext, onLoadMore, people.length])

    return (
        <Card
            className={`${shellStyles.cardBase} ${styles.peopleCard}`.trim()}
            bordered={false}
        >
            <HomeCardHeader title="People" />
            <div className={shellStyles.cardBody}>
                <div className={shellStyles.scrollArea} ref={scrollRef}>
                    <div className={styles.peopleList}>
                        {peopleStatus === 'loading'
                            ? Array.from(
                                  { length: PEOPLE_ROW_SKELETON_COUNT },
                                  (_, rowIndex) => (
                                      <PeopleRowSkeleton
                                          key={`people-skeleton-${rowIndex}`}
                                          rowIndex={rowIndex}
                                      />
                                  )
                              )
                            : people.map((person, idx) => {
                                  const name =
                                      person?.name || person?.email || 'Unknown'
                                  const totals = person?.total || {}
                                  return (
                                      <div
                                          className={styles.peopleRow}
                                          key={
                                              person?._id ||
                                              `${person?.email}-${idx}`
                                          }
                                      >
                                          <div className={styles.peopleLeft}>
                                              <Avatar
                                                  size={34}
                                                  className={
                                                      styles.peopleAvatar
                                                  }
                                                  src={person?.profilePicture}
                                              >
                                                  {name
                                                      ?.split(' ')?.[0]?.[0]
                                                      ?.toUpperCase()}
                                                  {(
                                                      name?.split(
                                                          ' '
                                                      )?.[1]?.[0] || ''
                                                  ).toUpperCase()}
                                              </Avatar>
                                              <div
                                                  className={styles.peopleMeta}
                                              >
                                                  <Text
                                                      className={
                                                          styles.peopleName
                                                      }
                                                  >
                                                      {name}
                                                  </Text>
                                                  <Text
                                                      className={
                                                          styles.peopleEmail
                                                      }
                                                  >
                                                      {person?.email || ''}
                                                  </Text>
                                              </div>
                                          </div>
                                          <Space size={6}>
                                              <Tag
                                                  bordered={false}
                                                  className={
                                                      styles.statTagOverdue
                                                  }
                                              >
                                                  {Number(totals?.overdue) || 0}{' '}
                                                  overdue
                                              </Tag>
                                              <Tag
                                                  bordered={false}
                                                  className={
                                                      styles.statTagCompleted
                                                  }
                                              >
                                                  {Number(totals?.completed) ||
                                                      0}{' '}
                                                  completed
                                              </Tag>
                                              <Tag
                                                  bordered={false}
                                                  className={
                                                      styles.statTagUpcoming
                                                  }
                                              >
                                                  {Number(totals?.upcoming) ||
                                                      0}{' '}
                                                  upcoming
                                              </Tag>
                                          </Space>
                                      </div>
                                  )
                              })}
                    </div>
                    {peopleStatus === 'error' ? (
                        <div className={shellStyles.errorWrap}>
                            <Text className={shellStyles.errorText}>
                                {peopleError}
                            </Text>
                            <button
                                type="button"
                                className={shellStyles.retryBtn}
                                onClick={onRetry}
                            >
                                Retry
                            </button>
                        </div>
                    ) : null}
                    {peopleStatus !== 'loading' &&
                    people.length === 0 &&
                    peopleStatus !== 'error' ? (
                        <Text className={shellStyles.stateText}>
                            No people available
                        </Text>
                    ) : null}
                    {peopleStatus === 'loadingMore' ? (
                        <div
                            className={styles.loadMoreLoader}
                            role="status"
                            aria-label="Loading more people"
                        >
                            <Icon
                                icon="line-md:loading-loop"
                                style={{ fontSize: 22, color: '#6366f1' }}
                                aria-hidden
                            />
                        </div>
                    ) : null}

                    {peopleHasNext && peopleStatus !== 'error' ? (
                        <div
                            ref={sentinelRef}
                            className={shellStyles.sentinel}
                            aria-hidden="true"
                        />
                    ) : null}
                </div>
            </div>
        </Card>
    )
}

export default PeopleCard
